import React, { FC, useState } from 'react';
import { AxiosError } from 'axios';
import apiClient from '../api/axiosConfig';
import type { PartyData } from '../types';
import './PartyCreation.scss';

interface PartyCreationProps {
  onPartyCreated: (data: PartyData) => void;
  onBack: () => void;
}

interface CreatePartyResponse {
  partyId: string;
  partyName: string;
}

const PartyCreation: FC<PartyCreationProps> = ({ onPartyCreated, onBack }) => {
  const [partyName, setPartyName] = useState<string>('');
  const [hostUsername, setHostUsername] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    if (!partyName.trim() || !hostUsername.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post<CreatePartyResponse>('/api/party/create', { partyName });
      const normalizedPartyId = (response.data?.partyId ?? '').toLowerCase();

      if (!normalizedPartyId) throw new Error('No partyId received from server');

      onPartyCreated({
        partyId: normalizedPartyId,
        partyName: response.data.partyName,
        hostUsername,
      });
    } catch (err) {
      const axiosErr = err as AxiosError<{ error?: string }>;
      setError(axiosErr.response?.data?.error ?? 'Failed to create party');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="party-creation">
      <div className="form-container">
        <h2>Create Party</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="partyName">Party Name</label>
            <input
              type="text"
              id="partyName"
              value={partyName}
              onChange={(e) => setPartyName(e.target.value)}
              placeholder="Enter party name"
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="hostUsername">Your Username</label>
            <input
              type="text"
              id="hostUsername"
              value={hostUsername}
              onChange={(e) => setHostUsername(e.target.value)}
              placeholder="Enter your username"
              disabled={loading}
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onBack} disabled={loading}>
              Back
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create Party'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartyCreation;
